import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, Slider, Stack, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { ManualPlayChrome, useManualPlay, usePlayKeyboard } from '../manualPlay';
import TwoJointPoleSceneMultiplayer from '../visuals/TwoJointPoleSceneMultiplayer';
import { TjpSwitchableScene } from '../visuals/TjpSceneView';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE, API_PATH_PROXY_ENABLED } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import { DEFAULT_STIFFNESS, DEFAULT_GRAVITY, DEFAULT_POLE_MASS, PHYSICS_PRESETS, clamp01, clampStiffness, gravityMs2, poleMassKg } from './TwoJointPole';
import { chooseBotAction, botDifficultyFromPhysicsPreset } from './TwoJointPolePlayer2Bot';
import { useTransientMessage } from '../useTransientMessage';
import TwoJointPoleModePicker from './TwoJointPoleModePicker';
import { COLOR } from '../ui/tokens';
import { acquireGmLease, endGmSession, gmStartErrorMessage, trackGmSession } from '../gmLease';
import { GAME_ACCENT } from '../ui/tokens';

const GAME_ID = 'two_joint_pole_multiplayer';
/** Multiplayer runs on the shared Two-Joint Pole GM (lease key, gm_registry.py). */
const GM_LEASE_ID = 'two_joint_pole';
const UPLOAD_URL = ARCADE.two_joint_pole?.UPLOAD_URL || 'http://localhost:3000';
const ACCENT = GAME_ACCENT.two_joint_pole;
const DT = 0.02;
/**
 * Multiplayer GM base URL.
 * Dev: same-origin CRA proxy `/tjp` → 127.0.0.1:5090 (src/setupProxy.js).
 * Prod path-proxy: ARCADE.two_joint_pole.API_BASE (/svc/two-joint-pole).
 * Never hardcode 127.0.0.1 — browsers opened via hostname/FQDN cannot reach it.
 */
const GM_BASE = API_PATH_PROXY_ENABLED
  ? (ARCADE.two_joint_pole?.API_BASE || '')
  : '/tjp';
/** Seconds to wait for a human opponent before falling back to vs-bot (GM LOBBY_TIMEOUT_S). */
const LOBBY_TIMEOUT_S = 20;

const initialMultiplayerPhysics = () => ({
  player_1: {
    cart_position: -1.0,
    cart_velocity: 0,
    pole1_angle: (Math.random() - 0.5) * 0.1,
    pole1_velocity: 0,
    pole2_angle: (Math.random() - 0.5) * 0.16,
    pole2_velocity: 0,
    punch_state: 0,
    punch_cooldown: 0,
  },
  player_2: {
    cart_position: 1.0,
    cart_velocity: 0,
    pole1_angle: (Math.random() - 0.5) * 0.1,
    pole1_velocity: 0,
    pole2_angle: (Math.random() - 0.5) * 0.16,
    pole2_velocity: 0,
    punch_state: 0,
    punch_cooldown: 0,
  },
});

const matchPhysicsPresetId = (stiffness, gravity, poleMass) => {
  const id = Object.keys(PHYSICS_PRESETS).find((key) => {
    const p = PHYSICS_PRESETS[key];
    return (
      Math.abs(p.stiffness - stiffness) < 1e-6 &&
      Math.abs(p.gravity - gravity) < 1e-6 &&
      Math.abs(p.poleMass - poleMass) < 1e-6
    );
  });
  return id || 'hard';
};
const OPPONENT_TOGGLE_SX = {
  flex: 1,
  textTransform: 'none',
  fontWeight: 600,
  color: COLOR.textMuted,
  borderColor: COLOR.borderStrong,
  '&.Mui-selected, &.Mui-selected:hover': {
    color: COLOR.text,
    bgcolor: 'rgba(142,140,216,0.18)',
    borderColor: ACCENT,
  },
};

const TwoJointPoleMultiplayer = ({ gameMode, setGameMode }) => {
  const play = useManualPlay();
  // 1v1_bot: vs computer (bot drives player_2); 1v1_local: same-keyboard 2P
  const [modeGameMode, setModeGameMode] = useState('1v1_bot');
  const [sessionId, setSessionId] = useState(null);
  const [playerPhysics, setPlayerPhysics] = useState(initialMultiplayerPhysics);
  const [playerShake, setPlayerShake] = useState({ player_1: 0, player_2: 0 });
  const [stepCount, setStepCount] = useState(0);
  const [playerScores, setPlayerScores] = useState({ player_1: 0, player_2: 0 });
  const [isGameOver, setIsGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  const [message, setMessage] = useTransientMessage();
  const [stiffness, setStiffness] = useState(DEFAULT_STIFFNESS);
  const [gravity, setGravity] = useState(DEFAULT_GRAVITY);
  const [poleMass, setPoleMass] = useState(DEFAULT_POLE_MASS);
  const [, setRunConfig] = useState({ stiffness, gravity, poleMass });
  /** Lobby: waiting | matched | bot_fallback | null (not in lobby / local). */
  const [lobbyStatus, setLobbyStatus] = useState(null);
  const [lobbySecondsLeft, setLobbySecondsLeft] = useState(LOBBY_TIMEOUT_S);
  /** After lobby resolves, true if opponent is a bot (vs computer). */
  const [vsBot, setVsBot] = useState(true);
  const [trainingStats, setTrainingStats] = useState(null);
  const [sessionMode, setSessionMode] = useState('match');

  const physicsRef = useRef(playerPhysics);
  const metaRef = useRef({ stepCount: 0, over: false });
  const actionRef = useRef({ player_1: 'stay', player_2: 'stay' });
  /** Latch Space so a short tap still registers as punch before the next GM tick. */
  const punchLatchRef = useRef({ player_1: false, player_2: false });
  const phaseRef = useRef(play.phase);
  const sessionIdRef = useRef(sessionId);
  const lockedParamsRef = useRef({ stiffness, gravity, poleMass });
  const vsBotRef = useRef(true);
  const lobbyPollRef = useRef(null);

  const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
  const recorder = useManualRunRecorder({ gameId: GAME_ID, session });

  useEffect(() => { physicsRef.current = playerPhysics; }, [playerPhysics]);
  useEffect(() => { phaseRef.current = play.phase; }, [play.phase]);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { vsBotRef.current = vsBot; }, [vsBot]);

  const clearLobbyPoll = useCallback(() => {
    if (lobbyPollRef.current) {
      clearInterval(lobbyPollRef.current);
      lobbyPollRef.current = null;
    }
  }, []);

  const applySessionPhysics = useCallback((data) => {
    if (!data?.players?.player_1?.physics || !data?.players?.player_2?.physics) return null;
    const p = {
      player_1: { ...data.players.player_1.physics },
      player_2: { ...data.players.player_2.physics },
    };
    setPlayerPhysics(p);
    physicsRef.current = p;
    setPlayerShake({
      player_1: Number(data.players.player_1.shake) || 0,
      player_2: Number(data.players.player_2.shake) || 0,
    });
    setStepCount(data.step_count || 0);
    setPlayerScores({
      player_1: data.players.player_1.score || 0,
      player_2: data.players.player_2.score || 0,
    });
    setIsGameOver(Boolean(data.hard_game_over ?? data.session_over ?? data.is_game_over));
    setWinner(data.winner_id || null);
    setSessionMode(data.mode || 'match');
    if (data.training_stats) {
      setTrainingStats(data.training_stats);
    } else if (data.mode !== 'training') {
      setTrainingStats(null);
    }
    metaRef.current = {
      stepCount: data.step_count || 0,
      over: Boolean(data.hard_game_over ?? data.session_over ?? data.is_game_over),
    };
    actionRef.current = { player_1: 'stay', player_2: 'stay' };
    punchLatchRef.current = { player_1: false, player_2: false };
    return p;
  }, []);

  const beginPlayingFromLobby = useCallback((data) => {
    clearLobbyPoll();
    const status = data.lobby_status || 'none';
    const bot = status === 'bot_fallback'
      || data.players?.player_2?.role === 'bot';
    setVsBot(bot);
    vsBotRef.current = bot;
    setLobbyStatus(status === 'none' ? null : status);
    setLobbySecondsLeft(0);
    const p = applySessionPhysics(data);
    if (p) {
      setMessage(
        bot
          ? 'No opponent joined — playing vs computer. Last upright pole wins!'
          : 'Opponent joined — human vs human. Last upright pole wins!',
      );
      recorder.reset({
        init_state: {
          players: {
            player_1: { ...p.player_1 },
            player_2: { ...p.player_2 },
          },
        },
      });
    }
    play.startGame();
  }, [applySessionPhysics, clearLobbyPoll, play, recorder, setMessage]);

  const createSession = useCallback(async ({ useLobby = false } = {}) => {
    try {
      // Starts the shared TJP GM if needed ("Starting game…"); the lobby and
      // the match hold the lease until game over / cancel / leaving the page.
      await acquireGmLease(GM_LEASE_ID);
      const res = await fetch(`${GM_BASE}/session/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          num_players: 2,
          stiffness: lockedParamsRef.current.stiffness,
          gravity: lockedParamsRef.current.gravity,
          pole_mass: lockedParamsRef.current.poleMass,
          lobby: useLobby,
          lobby_timeout_s: LOBBY_TIMEOUT_S,
          player_roles: useLobby
            ? { player_1: 'human', player_2: 'bot' }
            : (modeGameMode === '1v1_bot'
              ? { player_1: 'human', player_2: 'bot' }
              : { player_1: 'human', player_2: 'human' }),
        }),
      });
      if (!res.ok) throw new Error('Failed to create session');
      const data = await res.json();
      trackGmSession(GM_LEASE_ID, data.session_id);
      setSessionId(data.session_id);
      sessionIdRef.current = data.session_id;
      return data;
    } catch (e) {
      console.error('Create session error:', e);
      setMessage(e?.isGmStartError ? gmStartErrorMessage(e) : `Error creating session: ${e.message}`);
      return null;
    }
  }, [modeGameMode, setMessage]);

  const cancelLobby = useCallback(async () => {
    clearLobbyPoll();
    const sid = sessionIdRef.current;
    if (sid) {
      endGmSession(GM_LEASE_ID, sid);
      try {
        await fetch(`${GM_BASE}/session/${sid}/lobby/cancel`, { method: 'POST' });
        await fetch(`${GM_BASE}/session/${sid}/delete`, { method: 'POST' });
      } catch (e) {
        console.error('Cancel lobby error:', e);
      }
    }
    setSessionId(null);
    sessionIdRef.current = null;
    setLobbyStatus(null);
    setLobbySecondsLeft(LOBBY_TIMEOUT_S);
    setMessage('Lobby cancelled.');
    play.endGame();
  }, [clearLobbyPoll, play, setMessage]);

  const initLocal = useCallback(() => {
    const p = initialMultiplayerPhysics();
    setPlayerPhysics(p);
    physicsRef.current = p;
    setPlayerShake({ player_1: 0, player_2: 0 });
    setStepCount(0);
    setPlayerScores({ player_1: 0, player_2: 0 });
    setIsGameOver(false);
    setWinner(null);
    setMessage('Episode initialized. Last upright pole wins — knock the opponent down!');
    metaRef.current = { stepCount: 0, over: false };
    actionRef.current = { player_1: 'stay', player_2: 'stay' };
    punchLatchRef.current = { player_1: false, player_2: false };
    recorder.reset({
      init_state: {
        players: {
          player_1: { ...p.player_1 },
          player_2: { ...p.player_2 },
        },
      },
    });
  }, [recorder, setMessage]);

  const startGame = useCallback(async () => {
    const locked = {
      stiffness: clampStiffness(stiffness),
      gravity: clamp01(gravity, DEFAULT_GRAVITY),
      poleMass: clamp01(poleMass, DEFAULT_POLE_MASS),
    };
    lockedParamsRef.current = locked;
    setRunConfig(locked);
    initLocal();
    clearLobbyPoll();

    // Local 2P: immediate human vs human on one keyboard (no lobby).
    if (modeGameMode === '1v1_local') {
      setVsBot(false);
      vsBotRef.current = false;
      setLobbyStatus(null);
      const data = await createSession({ useLobby: false });
      if (!data?.session_id) {
        play.markOver();
        return;
      }
      applySessionPhysics(data);
      setMessage('Episode initialized. Last upright pole wins — knock the opponent down!');
      play.startGame();
      return;
    }

    // Vs computer: the bot plays player_2 right away. Human vs human on two
    // computers is the separate "Online 2P" mode (server-ticked gateway rooms,
    // TwoJointPoleOnline.jsx); the GM's HTTP step refuses matched human seats.
    setVsBot(true);
    vsBotRef.current = true;
    setLobbyStatus(null);
    const data = await createSession({ useLobby: false });
    if (!data?.session_id) {
      play.markOver();
      return;
    }
    beginPlayingFromLobby(data);
    setMessage('Vs computer. Last upright pole wins — knock the bot down!');
  }, [
    gravity, initLocal, play, poleMass, stiffness, createSession, modeGameMode,
    applySessionPhysics, clearLobbyPoll, beginPlayingFromLobby,
    setMessage,
  ]);

  const endGame = useCallback(() => {
    const wasLive = play.phase === 'playing' || play.phase === 'paused';
    clearLobbyPoll();
    setLobbyStatus(null);
    play.endGame(wasLive ? {
      score: `${playerScores.player_1.toFixed(1)} – ${playerScores.player_2.toFixed(1)}`,
      scoreLabel: 'P1 – P2',
    } : undefined);
    initLocal();
  }, [initLocal, play, clearLobbyPoll, playerScores]);

  useEffect(() => () => clearLobbyPoll(), [clearLobbyPoll]);
  const resolveBotAction = useCallback((ownPhysics, opponentPhysics) => {
    const locked = lockedParamsRef.current;
    const presetId = matchPhysicsPresetId(locked.stiffness, locked.gravity, locked.poleMass);
    const difficulty = botDifficultyFromPhysicsPreset(presetId);
    return chooseBotAction(ownPhysics, opponentPhysics, { difficulty });
  }, []);

  const tickBusyRef = useRef(false);

  const tick = useCallback(async () => {
    if (metaRef.current.over || tickBusyRef.current) return;
    if (lobbyStatus === 'waiting') return;

    const sid = sessionIdRef.current;
    if (!sid) return;

    tickBusyRef.current = true;
    const currentPhys = physicsRef.current;
    const actions = { ...actionRef.current };

    if (punchLatchRef.current.player_1) {
      actions.player_1 = 'punch';
      punchLatchRef.current.player_1 = false;
    }
    if (modeGameMode === '1v1_local' && punchLatchRef.current.player_2) {
      actions.player_2 = 'punch';
      punchLatchRef.current.player_2 = false;
    }

    // Bot is player_2 when vs computer (lobby bot_fallback or explicit bot mode).
    if (vsBotRef.current && modeGameMode !== '1v1_local') {
      try {
        actions.player_2 = resolveBotAction(currentPhys.player_2, currentPhys.player_1);
      } catch (e) {
        console.error('Bot action error:', e);
        actions.player_2 = 'stay';
      }
    }

    try {
      const res = await fetch(`${GM_BASE}/session/${sid}/step`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actions }),
      });

      if (!res.ok) {
        console.error('Step failed:', res.status);
        return;
      }

      const data = await res.json();
      const newPhysics = {
        player_1: data.players.player_1.physics,
        player_2: data.players.player_2.physics,
      };

      const scores = {
        player_1: data.players.player_1.score,
        player_2: data.players.player_2.score,
      };

      setPlayerPhysics(newPhysics);
      setPlayerShake({
        player_1: Number(data.players.player_1.shake) || 0,
        player_2: Number(data.players.player_2.shake) || 0,
      });
      setStepCount(data.step_count);
      setPlayerScores(scores);
      physicsRef.current = newPhysics;
      metaRef.current.stepCount = data.step_count;
      if (data.mode) setSessionMode(data.mode);
      if (data.training_stats) setTrainingStats(data.training_stats);

      // Replay tick: both carts' physics (incl. punch_state), scores, actions.
      recorder.recordTick({
        step: data.step_count,
        round_index: data.round_index,
        actions: { ...actions },
        players: {
          player_1: {
            physics: { ...newPhysics.player_1 },
            score: scores.player_1,
            is_alive: data.players?.player_1?.is_alive !== false,
            shake: Number(data.players.player_1.shake) || 0,
            hit_impulse: Number(data.players.player_1.hit_impulse) || 0,
          },
          player_2: {
            physics: { ...newPhysics.player_2 },
            score: scores.player_2,
            is_alive: data.players?.player_2?.is_alive !== false,
            shake: Number(data.players.player_2.shake) || 0,
            hit_impulse: Number(data.players.player_2.hit_impulse) || 0,
          },
        },
        scores: { ...scores },
        is_game_over: Boolean(data.hard_game_over ?? data.session_over ?? data.is_game_over),
        round_over: Boolean(data.round_over),
        winner_id: data.winner_id || null,
      });

      const hardOver = Boolean(data.hard_game_over ?? data.session_over ?? data.is_game_over);
      if (data.round_over && !hardOver) {
        setWinner(data.winner_id);
        setMessage(data.message || `Round ${data.rounds_completed} over`);
        // Soft: keep playing — next tick GM advances to next round.
      }
      if (hardOver) {
        endGmSession(GM_LEASE_ID, sid);
        setIsGameOver(true);
        setWinner(data.winner_id);
        setMessage(`Game over: ${data.terminated_reason}`);
        metaRef.current.over = true;
        play.markOver();

        recorder.finalizeAndUpload({
          score: Math.max(scores.player_1, scores.player_2),
          extra: {
            winner: data.winner_id,
            player_1_score: scores.player_1,
            player_2_score: scores.player_2,
            steps: data.step_count,
            terminated_reason: data.terminated_reason,
            stiffness: lockedParamsRef.current.stiffness,
            gravity: lockedParamsRef.current.gravity,
            pole_mass: lockedParamsRef.current.poleMass,
          },
        });
      } else if (!(data.round_over)) {
        trackGmSession(GM_LEASE_ID, sid);
        setMessage('Game running.');
      }
    } catch (e) {
      console.error('Step error:', e);
    } finally {
      tickBusyRef.current = false;
    }
  }, [modeGameMode, resolveBotAction, play, recorder, lobbyStatus, setMessage]);

  /** Optimistic punch arm + immediate GM step so Space → punch_state/impulses same frame. */
  const latchPunch = useCallback((playerKey) => {
    punchLatchRef.current[playerKey] = true;
    const next = {
      ...physicsRef.current,
      [playerKey]: {
        ...physicsRef.current[playerKey],
        punch_state: 1,
      },
    };
    physicsRef.current = next;
    setPlayerPhysics(next);
    // Fire GM step immediately (interval also ticks; reentrancy guard prevents double-step).
    void tick();
  }, [tick]);

  useEffect(() => {
    if (play.phase !== 'playing') return undefined;
    const id = setInterval(() => {
      if (phaseRef.current !== 'playing') return;
      tick();
    }, DT * 1000);
    return () => clearInterval(id);
  }, [play.phase, tick]);

  const onKey = useCallback((e) => {
    if (modeGameMode === '1v1_local') {
      // P1: arrows · P2: A/D · punches: Space (P1) / Shift (P2)
      if (e.key === 'ArrowLeft') {
        actionRef.current.player_1 = 'left';
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        actionRef.current.player_1 = 'right';
        e.preventDefault();
      } else if (e.key === ' ') {
        latchPunch('player_1');
        e.preventDefault();
      } else if (e.key === 'a' || e.key === 'A') {
        actionRef.current.player_2 = 'left';
        e.preventDefault();
      } else if (e.key === 'd' || e.key === 'D') {
        actionRef.current.player_2 = 'right';
        e.preventDefault();
      } else if (e.key === 'Shift') {
        latchPunch('player_2');
        e.preventDefault();
      }
      return;
    }

    if (['ArrowLeft', 'a', 'A'].includes(e.key)) {
      actionRef.current.player_1 = 'left';
      e.preventDefault();
    } else if (['ArrowRight', 'd', 'D'].includes(e.key)) {
      actionRef.current.player_1 = 'right';
      e.preventDefault();
    } else if (e.key === ' ') {
      latchPunch('player_1');
      e.preventDefault();
    }
  }, [modeGameMode, latchPunch]);

  useEffect(() => {
    const up = (e) => {
      if (modeGameMode === '1v1_local') {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          actionRef.current.player_1 = 'stay';
        } else if (e.key === 'a' || e.key === 'A' || e.key === 'd' || e.key === 'D') {
          actionRef.current.player_2 = 'stay';
        }
        return;
      }
      if (['ArrowLeft', 'a', 'A', 'ArrowRight', 'd', 'D'].includes(e.key)) {
        actionRef.current.player_1 = 'stay';
      }
      // Do not clear punch on Space keyup — latch is consumed by the next tick.
    };
    window.addEventListener('keyup', up);
    return () => window.removeEventListener('keyup', up);
  }, [modeGameMode]);

  usePlayKeyboard({
    phase: play.phase,
    togglePause: play.togglePause,
    onKey,
    deps: [modeGameMode],
  });

  const HINT = modeGameMode === '1v1_local'
    ? 'P1: ← → · Space punch · P2: A D · Shift punch · Esc pause'
    : '← → / A D move · Space punch · Esc pause';

  const resultBanner = useMemo(() => {
    const reason = String(message || '');
    const isDraw = /draw/i.test(reason) || (!winner && /all poles down/i.test(reason));
    if (!winner) {
      return {
        won: false,
        overTitle: isDraw ? 'Draw!' : 'Game Over',
        subtitle: isDraw
          ? 'Both poles hit the ground — simultaneous fall.'
          : (message || 'No winner this round.'),
      };
    }
    if (vsBot || modeGameMode === '1v1_bot') {
      const humanWon = winner === 'player_1';
      return {
        won: humanWon,
        overTitle: humanWon ? 'You win!' : 'You lose',
        subtitle: humanWon
          ? 'Last pole standing — you stayed up and they went down.'
          : 'The bot took the round. Hit Play Again below to rematch.',
      };
    }
    const label = winner === 'player_1' ? 'P1' : winner === 'player_2' ? 'P2' : winner;
    return {
      won: true,
      overTitle: `${label} Wins!`,
      subtitle: message || `${label} last one standing.`,
    };
  }, [winner, modeGameMode, message, vsBot]);

  const inLobby = lobbyStatus === 'waiting';

  return (
    <ArcadeGameShell
      title="Two-Joint"
      titleAccent="Pole"
      subtitle="Two-player competitive mode — last upright pole wins"
      accentColor={ACCENT}
      headerContent={(
        <TwoJointPoleModePicker
          value={gameMode}
          onChange={setGameMode}
          accentColor={ACCENT}
          disabled={play.phase === 'playing' || play.phase === 'paused' || inLobby}
        />
      )}
      boilerplate={GAME_BOILERPLATE.two_joint_pole}
      session={session}
      formatScore={(e) => {
        const v = e.player_1_score ?? e.score ?? 0;
        return typeof v === 'number' ? v.toFixed?.(1) ?? String(v) : String(v);
      }}
    >
      <ManualPlayChrome
        phase={play.phase}
        result={play.result}
        onStart={startGame}
        onPause={play.togglePause}
        onEnd={endGame}
        accentColor={ACCENT}
        controlsHint={HINT}
        minHeight={520}
        showOverlayPlayAgain={false}
        won={resultBanner.won}
        overTitle={resultBanner.overTitle}
        overContent={
          play.phase === 'over' ? (
            <Typography
              sx={{
                color: resultBanner.won ? '#ffe066' : COLOR.textMuted,
                fontWeight: 600,
                textAlign: 'center',
                maxWidth: 360,
                zIndex: 1,
                textShadow: resultBanner.won ? '0 0 18px rgba(255,224,102,0.35)' : 'none',
              }}
            >
              {resultBanner.subtitle}
              <Box
                component="span"
                data-testid="tjp-final-score"
                className="evx-tabular"
                sx={{ display: 'block', mt: 1, color: COLOR.text, fontWeight: 800, fontSize: '1.4rem' }}
              >
                P1 {playerScores.player_1.toFixed(1)} – P2 {playerScores.player_2.toFixed(1)}
              </Box>
            </Typography>
          ) : null
        }
        setupContent={
          <Box
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
              width: '100%',
              maxWidth: 420,
              p: 2,
              borderRadius: 2,
              border: '1px solid rgba(142,140,216,0.35)',
              bgcolor: 'rgba(12, 16, 32, 0.92)',
            }}
          >
            <Typography sx={{ color: COLOR.text, fontWeight: 700, mb: 0.5 }}>
              Opponent
            </Typography>
            <ToggleButtonGroup
              value={modeGameMode}
              exclusive
              onChange={(_, v) => v && setModeGameMode(v)}
              sx={{ mb: 2, width: '100%' }}
            >
              <ToggleButton value="1v1_bot" sx={OPPONENT_TOGGLE_SX}>Vs computer</ToggleButton>
              <ToggleButton value="1v1_local" sx={OPPONENT_TOGGLE_SX}>Local 2P</ToggleButton>
            </ToggleButtonGroup>
            <Typography sx={{ color: COLOR.textSubtle, fontSize: '0.75rem', mb: 1.5 }}>
              Playing someone on another computer? Pick Online 2P at the top.
            </Typography>

            <Typography sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
              Episode setup
            </Typography>
            <Typography sx={{ color: COLOR.textMuted, fontSize: '0.8rem', mb: 1.5 }}>
              Pick a pack or fine-tune. Values lock when the episode starts.
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
              {Object.entries(PHYSICS_PRESETS).map(([id, p]) => (
                <Chip
                  key={id}
                  label={p.label}
                  clickable
                  onClick={() => {
                    setStiffness(p.stiffness);
                    setGravity(p.gravity);
                    setPoleMass(p.poleMass);
                  }}
                  sx={{
                    color: ACCENT,
                    borderColor: ACCENT,
                  }}
                  variant="outlined"
                />
              ))}
            </Stack>
            <Stack spacing={1}>
              <PlaySlider
                label={`Joint stiffness · ${Math.round(stiffness * 100)}%`}
                value={stiffness}
                onChange={setStiffness}
              />
              <PlaySlider
                label={`Gravity · ${gravityMs2(gravity).toFixed(1)} m/s²`}
                value={gravity}
                onChange={setGravity}
              />
              <PlaySlider
                label={`Pole weight · ${poleMassKg(poleMass).toFixed(3)} kg`}
                value={poleMass}
                onChange={setPoleMass}
              />
            </Stack>
          </Box>
        }
      >
        <Box sx={{ p: 2, position: 'relative' }}>
          {inLobby && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(6, 8, 14, 0.92)',
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 360,
                  p: 3,
                  borderRadius: 2,
                  border: '1px solid rgba(142,140,216,0.4)',
                  bgcolor: 'rgba(12, 16, 32, 0.98)',
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ color: COLOR.text, fontWeight: 700, fontSize: '1.15rem', mb: 1 }}>
                  Waiting for opponent…
                </Typography>
                <Typography sx={{ color: COLOR.textMuted, fontSize: '0.85rem', mb: 2 }}>
                  Match starts when someone joins, or vs computer after timeout.
                </Typography>
                <Typography
                  sx={{
                    color: ACCENT,
                    fontWeight: 800,
                    fontSize: '2.4rem',
                    fontVariantNumeric: 'tabular-nums',
                    mb: 0.5,
                  }}
                >
                  {Math.max(0, Math.ceil(lobbySecondsLeft))}
                </Typography>
                <Typography sx={{ color: COLOR.textSubtle, fontSize: '0.75rem', mb: 2.5 }}>
                  seconds remaining · lobby timeout {LOBBY_TIMEOUT_S}s
                </Typography>
                <Button
                  variant="outlined"
                  onClick={cancelLobby}
                  sx={{
                    color: '#f87171',
                    borderColor: 'rgba(248,113,113,0.5)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#f87171',
                      bgcolor: 'rgba(248,113,113,0.08)',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip label={`Steps: ${stepCount}`} size="small" sx={{ color: ACCENT, borderColor: ACCENT }} variant="outlined" />
              <Chip label={`P1: ${playerScores.player_1.toFixed(1)}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
              <Chip label={`P2: ${playerScores.player_2.toFixed(1)}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
              <Chip label={isGameOver ? 'Done' : (inLobby ? 'Lobby' : 'Active')} size="small" color={isGameOver ? 'warning' : 'success'} />
              {winner && (
                <Chip
                  label={
                    vsBot
                      ? (winner === 'player_1' ? 'You win' : 'You lose')
                      : `Winner: ${winner === 'player_1' ? 'P1' : 'P2'}`
                  }
                  size="small"
                  color={vsBot && winner !== 'player_1' ? 'error' : 'success'}
                  variant="filled"
                />
              )}
            </Stack>
          </Box>
          {sessionMode === 'training' && trainingStats && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5, flexWrap: 'wrap', gap: 0.75 }}>
              <Chip
                size="small"
                variant="filled"
                label={`Train ${trainingStats.round_index || 1}/${trainingStats.num_episodes || '?'}`}
                sx={{ bgcolor: 'rgba(56,189,248,0.2)', color: '#7dd3fc' }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`W ${trainingStats.wins?.player_1 || 0}–${trainingStats.wins?.player_2 || 0} D ${trainingStats.draws || 0}`}
                sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Avg ${(trainingStats.avg_scores?.player_1 || 0).toFixed(0)} / ${(trainingStats.avg_scores?.player_2 || 0).toFixed(0)}`}
                sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }}
              />
              {typeof trainingStats.punch_rate === 'number' && trainingStats.total_actions > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Punch ${(100 * trainingStats.punch_rate).toFixed(0)}%`}
                  sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }}
                />
              )}
            </Box>
          )}
          {message && <Typography sx={{ color: COLOR.textMuted, mb: 1, fontSize: '0.85rem' }}>{message}</Typography>}
          <Box sx={{ borderRadius: 3, border: '1px solid rgba(142,140,216,0.3)', overflow: 'hidden', bgcolor: '#0c1a24' }}>
            <TjpSwitchableScene
              Scene2D={TwoJointPoleSceneMultiplayer}
              player1_physics={playerPhysics.player_1}
              player2_physics={playerPhysics.player_2}
              player1_shake={playerShake.player_1}
              player2_shake={playerShake.player_2}
              width={560}
              height={320}
            />
          </Box>
        </Box>
      </ManualPlayChrome>
    </ArcadeGameShell>
  );
};

export default TwoJointPoleMultiplayer;

function PlaySlider({ label, value, onChange }) {
  return (
    <Box>
      <Typography sx={{ color: COLOR.textMuted, fontSize: '0.8rem', mb: 0.5 }}>{label}</Typography>
      <Slider
        value={value}
        min={0}
        max={1}
        step={0.05}
        onChange={(_, v) => onChange(clamp01(Array.isArray(v) ? v[0] : v, value))}
        sx={{ color: ACCENT, py: 0.5 }}
      />
    </Box>
  );
}
